/**
 * Lightweight toast + confirm notices for gated actions (login, sold out, etc.)
 */
(function () {
  const STYLE_ID = 'october-action-notice-styles';

  const STYLES = `
    .october-action-notice {
      position: fixed;
      inset: 0;
      z-index: 10050;
      pointer-events: none;
    }

    .october-action-notice__toast,
    .october-action-notice__dialog {
      pointer-events: auto;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .october-action-notice__toast {
      position: fixed;
      left: 50%;
      bottom: calc(1.25rem + env(safe-area-inset-bottom));
      transform: translateX(-50%) translateY(120%);
      width: min(92vw, 420px);
      padding: 0.95rem 1.1rem;
      border-radius: 0.65rem;
      background: rgba(246, 239, 232, 0.98);
      border: 1px solid rgba(109, 74, 47, 0.22);
      box-shadow: 0 0.75rem 2rem rgba(70, 42, 22, 0.18);
      color: #3b2618;
      transition: transform 0.28s ease, opacity 0.28s ease;
      opacity: 0;
    }

    .october-action-notice__toast.is-visible {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }

    .october-action-notice__toast-title {
      margin: 0 0 0.35rem;
      font-size: 0.95rem;
      font-weight: 600;
      line-height: 1.3;
    }

    .october-action-notice__toast-message {
      margin: 0;
      font-size: 0.86rem;
      line-height: 1.45;
      color: #5c4030;
    }

    .october-action-notice__toast--error {
      border-color: rgba(139, 58, 42, 0.35);
    }

    .october-action-notice__backdrop {
      position: fixed;
      inset: 0;
      background: rgba(24, 16, 10, 0.42);
      opacity: 0;
      transition: opacity 0.24s ease;
    }

    .october-action-notice__backdrop.is-visible {
      opacity: 1;
    }

    .october-action-notice__dialog {
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -46%) scale(0.96);
      width: min(92vw, 420px);
      padding: 1.2rem 1.15rem 1rem;
      border-radius: 0.7rem;
      background: #fffef8;
      border: 1px solid rgba(109, 74, 47, 0.2);
      box-shadow: 0 1rem 2.5rem rgba(70, 42, 22, 0.2);
      opacity: 0;
      transition: transform 0.24s ease, opacity 0.24s ease;
    }

    .october-action-notice__dialog.is-visible {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }

    .october-action-notice__dialog-title {
      margin: 0 0 0.45rem;
      font-size: 1rem;
      font-weight: 600;
      color: #3b2618;
    }

    .october-action-notice__dialog-message {
      margin: 0 0 1rem;
      font-size: 0.88rem;
      line-height: 1.5;
      color: #5c4030;
    }

    .october-action-notice__actions {
      display: flex;
      gap: 0.55rem;
      justify-content: flex-end;
    }

    .october-action-notice__btn {
      border: 1px solid rgba(109, 74, 47, 0.28);
      background: transparent;
      color: #5c4030;
      border-radius: 999px;
      padding: 0.45rem 0.95rem;
      font-size: 0.82rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      cursor: pointer;
    }

    .october-action-notice__btn--primary {
      background: #66330f;
      border-color: #66330f;
      color: #fffef8;
    }
  `;

  let root;
  let toastTimer;

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  function ensureRoot() {
    injectStyles();
    if (root) return root;
    root = document.createElement('div');
    root.className = 'october-action-notice';
    root.setAttribute('aria-live', 'polite');
    document.body.appendChild(root);
    return root;
  }

  function show({ title, message, type = 'info', duration = 5200 }) {
    const host = ensureRoot();
    host.querySelectorAll('.october-action-notice__toast').forEach((node) => node.remove());

    const toast = document.createElement('div');
    toast.className = `october-action-notice__toast october-action-notice__toast--${type}`;
    toast.innerHTML = `
      <p class="october-action-notice__toast-title">${title || 'Notice'}</p>
      <p class="october-action-notice__toast-message">${message || ''}</p>
    `;
    host.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 280);
    }, duration);
  }

  function confirm({ title, message, confirmLabel = 'Sign in', cancelLabel = 'Not now', onConfirm }) {
    const host = ensureRoot();

    const backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'october-action-notice__backdrop';
    backdrop.setAttribute('aria-label', 'Close dialog');

    const dialog = document.createElement('div');
    dialog.className = 'october-action-notice__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.innerHTML = `
      <h3 class="october-action-notice__dialog-title">${title || 'Sign in required'}</h3>
      <p class="october-action-notice__dialog-message">${message || ''}</p>
      <div class="october-action-notice__actions">
        <button type="button" class="october-action-notice__btn" data-notice-cancel>${cancelLabel}</button>
        <button type="button" class="october-action-notice__btn october-action-notice__btn--primary" data-notice-confirm>${confirmLabel}</button>
      </div>
    `;

    host.appendChild(backdrop);
    host.appendChild(dialog);

    const close = () => {
      backdrop.classList.remove('is-visible');
      dialog.classList.remove('is-visible');
      window.setTimeout(() => {
        backdrop.remove();
        dialog.remove();
      }, 240);
    };

    backdrop.addEventListener('click', close);
    dialog.querySelector('[data-notice-cancel]').addEventListener('click', close);
    dialog.querySelector('[data-notice-confirm]').addEventListener('click', () => {
      close();
      if (typeof onConfirm === 'function') onConfirm();
    });

    requestAnimationFrame(() => {
      backdrop.classList.add('is-visible');
      dialog.classList.add('is-visible');
    });
  }

  window.octoberActionNotice = { show, confirm };
})();
